/**
 * User preferences types.
 * These settings are stored in cookies for persistence.
 *
 * Currency and UnitSystem are re-exported from the proto-generated
 * enums (api/proto/common/v1/{currency,unit_system}.proto).
 *
 * Wire ↔ canonical conversion at every boundary (cookie, URL,
 * legacy JSON) MUST go through the generated `wireToCanonical`
 * helper in `@letsgethome/lib/proto-enums/wire_aliases_gen`,
 * whose alias table is sourced from `api/manifests/proto-enums.yaml`
 * (`Currency.wire_aliases`, `UnitSystem.wire_aliases`). The
 * `*ToWire` helpers below cover the canonical-to-wire direction
 * (a single-element projection that does not require the alias
 * table — every variant has exactly one canonical wire form).
 */

import type {
  PROTO_ENUM_VALUE_SET_Currency,
  PROTO_ENUM_VALUE_SET_UnitSystem,
} from '@letsgethome/lib/proto-enums/value_sets_gen';
import { Currency } from '@letsgethome/proto/common/v1/currency_pb';
import { UnitSystem } from '@letsgethome/proto/common/v1/unit_system_pb';
import { DEFAULT_LOCALE, LOCALE_ENDONYMS, LOCALES, type Locale } from '@letsgethome/types/branded';

export type { Locale };
export { Currency, UnitSystem };

// Lowercase wire forms persisted in the preferences cookie. Derived from the
// proto-enum SSOT value-sets (Currency / UnitSystem in
// api/manifests/proto-enums.yaml) so they cannot drift from the canonical
// wire slugs — adding/renaming a variant flows through one regeneration.
export type CurrencyWire = (typeof PROTO_ENUM_VALUE_SET_Currency)[number];
export type UnitSystemWire = (typeof PROTO_ENUM_VALUE_SET_UnitSystem)[number];

export interface UserPreferences {
  /** Auto-translate listing content to user's preferred language */
  autoTranslate: boolean;
  /** Display language for the UI */
  locale: Locale;
  /** Currency for displaying prices */
  currency: Currency;
  /** Unit system for displaying area measurements */
  unitSystem: UnitSystem;
}

export const DEFAULT_PREFERENCES: UserPreferences = {
  autoTranslate: true,
  locale: DEFAULT_LOCALE,
  currency: Currency.HKD,
  unitSystem: UnitSystem.IMPERIAL,
};

// DERIVED from the generated Locale SSOT (`LOCALES` order + `LOCALE_ENDONYMS`
// display names, both from `common.v1.Locale` in api/manifests/proto-enums.yaml)
// — enabling a locale makes it a preferences option by construction, instead of
// a hand list that could silently miss it.
export const LOCALE_OPTIONS: readonly { value: Locale; label: string }[] = LOCALES.map(
  (locale) => ({ value: locale, label: LOCALE_ENDONYMS[locale] })
);

export const CURRENCY_OPTIONS: {
  value: Currency;
  labelKey: CurrencyWire;
  symbol: string;
}[] = [
  { value: Currency.HKD, labelKey: 'hkd', symbol: 'HK$' },
  { value: Currency.CNY, labelKey: 'cny', symbol: '¥' },
];

export const UNIT_SYSTEM_OPTIONS: {
  value: UnitSystem;
  labelKey: UnitSystemWire;
}[] = [
  { value: UnitSystem.IMPERIAL, labelKey: 'imperial' },
  { value: UnitSystem.METRIC, labelKey: 'metric' },
];

export function currencyToWire(value: Currency): CurrencyWire {
  return value === Currency.CNY ? 'cny' : 'hkd';
}

export function unitSystemToWire(value: UnitSystem): UnitSystemWire {
  return value === UnitSystem.METRIC ? 'metric' : 'imperial';
}
